import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  AbstractControl,
} from '@angular/forms';
import { AxiosError } from 'axios';
import { isValid, parseISO, startOfDay, startOfToday, subDays } from 'date-fns';
import {
  Subject,
  distinctUntilChanged,
  map,
  startWith,
  takeUntil,
} from 'rxjs';
import {
  claimType,
  claimTypeLegalRelationship,
} from 'src/app/components/dashboard/enums/enum-descriptors';
import {
  INTEREST_TYPE,
  InterestType,
} from 'src/app/services/cases/cases.service';
import { CurrencyService } from 'src/app/services/currency/currency.service';
import { EnumsService } from 'src/app/services/enums/enums.service';
import { SzamlazzhuService } from 'src/app/services/szamlazzhu/szamlazzhu.service';
import { SzamlazzhuInvoiceGroup } from 'src/app/services/szamlazzhu/types';
import { ClaimType } from 'src/types';
import { ClaimTypeLegalRelationship } from 'src/types/model/schemas';
import {
  CASE_TYPE,
  CaseType,
  ClaimControls,
  ClaimFileControls,
  EditCaseDialogService,
} from '../edit-case-dialog.service';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeButtonComponent } from '../../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeInputDirective } from '../../../shared/payee-input/payee-input.directive';
import { PayeeDatepickerComponent } from '../../../shared/payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeRadioGroupComponent } from '../../../shared/payee-radio/payee-radio-group/payee-radio-group.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ClaimFormComponent } from '../claim-form/claim-form.component';

@Component({
  selector: 'app-claims-form',
  templateUrl: './claims-form.component.html',
  styleUrls: ['./claims-form.component.scss'],
  imports: [
    PayeeFormFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeButtonComponent,
    PayeeInputDirective,
    PayeeDatepickerComponent,
    PayeeAutocompleteComponent,
    PayeeRadioGroupComponent,
    MatProgressSpinner,
    ClaimFormComponent,
  ],
})
export class ClaimsFormComponent implements OnInit, OnDestroy {
  private currencyService = inject(CurrencyService);
  private editCaseDialogService = inject(EditCaseDialogService);
  private enumsService = inject(EnumsService);
  private szamlazzhuService = inject(SzamlazzhuService);

  private readonly destroy = new Subject<void>();
  private readonly condominiumOcrProcessedKeys = new Set<string>();

  get claims() {
    return this.editCaseDialogService.form.controls.claims;
  }

  readonly loading = signal(false);
  claimTypeLegalRelationships: ClaimTypeLegalRelationship[] = [];
  claimTypes: ClaimType[] = [];
  claimTypeOptions: number[] = [];
  currencyOptions: string[] = [];
  szamlazzhuInvoices: SzamlazzhuInvoiceGroup[] = [];

  get interest_type() {
    return this.editCaseDialogService.form.controls.interest_type;
  }

  globalClaimType = new FormControl<number | null>(null);
  globalClaimTypeSpecification = new FormControl<string | null>(null);

  globalContractIdentification = new FormControl<string>('', {
    nonNullable: true,
  });
  globalContractDate = new FormControl<Date | string | null>(null);

  globalCaseType = new FormControl<CaseType>(CASE_TYPE.INVOICE, {
    nonNullable: true,
  });
  globalInterestRate = new FormControl<string>('', { nonNullable: true });

  readonly CASE_TYPE = CASE_TYPE;
  readonly INTEREST_TYPE = INTEREST_TYPE;
  readonly max = subDays(startOfToday(), 1);

  async ngOnInit(): Promise<void> {
    this.loading.set(true);
    await Promise.allSettled([
      this.currencyService
        .getCurrencies()
        .then((v) => (this.currencyOptions = v)),
      this.enumsService
        .getEnum<ClaimTypeLegalRelationship>(claimTypeLegalRelationship.model)
        .then((v) => (this.claimTypeLegalRelationships = v)),
      this.enumsService
        .getEnum<ClaimType>(claimType.model)
        .then((v) => (this.claimTypes = v)),
      this.setSzamlazzhuInvoices(),
    ]);
    this.loading.set(false);

    this.claimTypeOptions = this.claimTypes.map((ct) => ct.id);
    this.editCaseDialogService.claimTypes = this.claimTypes;
    this.editCaseDialogService.claimsGlobalControls = {
      caseType: this.globalCaseType,
      claimType: this.globalClaimType,
      contractIdentification: this.globalContractIdentification,
      contractDate: this.globalContractDate,
    };

    this.editCaseDialogService.form.controls.legal_relationship.controls.type_id.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(() => {
        this.filterClaimTypeOptions();
      });

    this.claims.valueChanges
      .pipe(
        takeUntil(this.destroy),
        startWith(this.claims.value),
        map((values) => values.map((v) => v.type_id).join(',')),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        void this.runCondominiumOcrForEligibleClaims();
      });

    this.hydrateGlobalsFromClaims();
    this.subscribeGlobalApplies();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  isAiFilled(control: AbstractControl | null | undefined): boolean {
    return this.editCaseDialogService.isAiFilled(control);
  }

  addClaim(): void {
    this.editCaseDialogService.addClaim();
    this.applyGlobalCaseType();
    this.applyGlobalContractIdentification();
    this.applyGlobalContractDate();
    this.applyGlobalInterestRate();
  }

  private hydrateGlobalsFromClaims(): void {
    const claims = this.claims.value;
    if (!claims.length) {
      return;
    }

    const firstCaseType = claims[0].files?.[0]?.case_type;
    if (
      firstCaseType &&
      claims.every((c) => c.files?.[0]?.case_type === firstCaseType)
    ) {
      this.globalCaseType.patchValue(firstCaseType, { emitEvent: false });
      this.editCaseDialogService.globalCaseType = firstCaseType;
    }

    const firstContractId = claims[0].files?.[0]?.invoice_number;
    if (
      firstContractId &&
      claims.every((c) => c.files?.[0]?.invoice_number === firstContractId)
    ) {
      this.globalContractIdentification.patchValue(firstContractId, {
        emitEvent: false,
      });
    }

    const firstContractDate = claims[0].files?.[0]?.invoice_date;
    if (
      firstContractDate &&
      claims.every((c) => {
        const claimDateRaw = c.files?.[0]?.invoice_date;
        const claimDate =
          claimDateRaw instanceof Date
            ? claimDateRaw
            : claimDateRaw
              ? parseISO(claimDateRaw)
              : null;
        const firstDate =
          firstContractDate instanceof Date
            ? firstContractDate
            : parseISO(firstContractDate);
        return (
          claimDate &&
          isValid(claimDate) &&
          isValid(firstDate) &&
          startOfDay(claimDate).getTime() === startOfDay(firstDate).getTime()
        );
      })
    ) {
      this.globalContractDate.patchValue(
        firstContractDate instanceof Date
          ? firstContractDate
          : parseISO(firstContractDate),
        { emitEvent: false },
      );
    }

    const firstClaimType = claims[0]?.type_id;
    if (firstClaimType && claims.every((c) => c.type_id === firstClaimType)) {
      this.globalClaimType.patchValue(firstClaimType, { emitEvent: false });
    }

    const firstClaimTypeSpec = claims[0]?.specification;
    if (
      firstClaimTypeSpec &&
      claims.every((c) => c.specification === firstClaimTypeSpec)
    ) {
      this.globalClaimTypeSpecification.patchValue(firstClaimTypeSpec, {
        emitEvent: false,
      });
    }
  }

  private subscribeGlobalApplies(): void {
    this.globalCaseType.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.applyGlobalCaseType());
    this.globalClaimTypeSpecification.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.applyGlobalSpecification());
    this.globalContractIdentification.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.applyGlobalContractIdentification());
    this.globalContractDate.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.applyGlobalContractDate());
    this.globalInterestRate.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe(() => this.applyGlobalInterestRate());
  }

  readonly claimTypeDisplayedWith = (id: number): string => {
    return this.claimTypes.find((ct) => ct.id === id)?.label || '';
  };

  showGlobalClaimTypeSpecification(): boolean {
    return this.claims.controls.some((c) => this.showClaimTypeSpecification(c));
  }

  applyGlobalClaimType(): void {
    const type_id = this.globalClaimType.value;
    this.claims.controls.forEach((control) => {
      control.patchValue({
        type_id,
      });
    });
  }

  applyGlobalSpecification(): void {
    const specification = this.globalClaimTypeSpecification.value;
    if (this.showGlobalClaimTypeSpecification()) {
      this.setClaimTypeSpecificationForAll(specification);
    }
  }

  applyGlobalCaseType(): void {
    const case_type = this.globalCaseType.value;
    this.editCaseDialogService.globalCaseType = case_type;

    this.claims.controls.forEach((control) => {
      control.controls.files.controls.forEach((fileControl) => {
        fileControl.patchValue({
          case_type,
        });
      });
    });
  }

  applyGlobalContractIdentification(): void {
    const invoice_number =
      this.globalContractIdentification.value.trim() || null;
    this.claims.controls.forEach((control) => {
      control.controls.files.controls.forEach((fileControl) => {
        fileControl.patchValue({
          invoice_number,
        });
      });
    });
  }

  applyGlobalContractDate(): void {
    const date = this.globalContractDate.value;
    this.claims.controls.forEach((control) => {
      control.controls.files.controls.forEach((fileControl) => {
        fileControl.patchValue({
          invoice_date: date,
        });
      });
    });
  }

  applyGlobalInterestType(interest_type: null | InterestType): void {
    this.interest_type.patchValue(interest_type);
    this.claims.controls.forEach((control) => {
      const interestClaim = control.controls.interest_claim;
      if (interest_type === 'contractual') {
        interestClaim.enable();
      } else {
        interestClaim.disable();
      }
    });
  }

  applyGlobalInterestRate(): void {
    const interest_rate = this.globalInterestRate.value;
    if (this.interest_type.value === INTEREST_TYPE.CONTRACTUAL) {
      this.claims.controls.forEach((control) => {
        control.controls.interest_claim.patchValue({
          interest_rate,
        });
      });
    }
  }

  private setClaimTypeSpecificationForAll(
    specification: string | null,
  ): void {
    this.claims.controls.forEach((control) => {
      if (this.showClaimTypeSpecification(control)) {
        control.patchValue({
          specification,
        });
      }
    });
  }

  private showClaimTypeSpecification(
    control: FormGroup<ClaimControls>,
  ): boolean {
    const value = control.value;
    if (!value.type_id) {
      return false;
    }

    const claimType = this.claimTypes.find((ct) => ct.id === value.type_id);
    return claimType?.internal_id === '99-a';
  }

  private async runCondominiumOcrForEligibleClaims(): Promise<void> {
    const fileControls = this.claims.controls
      .filter((claim) =>
        this.editCaseDialogService.isCondominiumCommonCostClaimType(
          claim.controls.type_id.value,
          this.claimTypes,
        ),
      )
      .flatMap((claim) => claim.controls.files.controls);

    await Promise.all(
      fileControls.map((fileControl) => this.runCondominiumOcrForFile(fileControl)),
    );
  }

  private async runCondominiumOcrForFile(
    fileControl: FormGroup<ClaimFileControls>,
  ): Promise<void> {
    const { id: mediaUuid, storage_url: storageUrl } = fileControl.getRawValue();
    const processKey =
      mediaUuid ??
      (storageUrl instanceof File
        ? `file:${storageUrl.name}:${storageUrl.size}:${storageUrl.lastModified}`
        : null);
    if (!processKey || this.condominiumOcrProcessedKeys.has(processKey)) {
      return;
    }

    this.condominiumOcrProcessedKeys.add(processKey);

    try {
      const ocrData = mediaUuid
        ? await this.editCaseDialogService.getCondominiumAccountStatementOcrByMedia(
            mediaUuid,
          )
        : storageUrl instanceof File
          ? await this.editCaseDialogService.getCondominiumAccountStatementOcr(
              storageUrl,
            )
          : null;

      if (!ocrData) {
        this.condominiumOcrProcessedKeys.delete(processKey);
        return;
      }

      this.editCaseDialogService.applyCondominiumAccountStatementOcr(
        fileControl,
        this.globalCaseType,
        ocrData,
      );
    } catch (error: AxiosError | unknown) {
      this.condominiumOcrProcessedKeys.delete(processKey);
      if (error instanceof AxiosError) {
        console.error(
          'Condominium account statement OCR error',
          error.response?.data,
        );
      } else {
        console.error('Condominium account statement OCR error', error);
      }
    }
  }

  private filterClaimTypeOptions(): void {
    const value = this.editCaseDialogService.form.getRawValue();
    const legal_relationship_id = value.legal_relationship.type_id;

    if (!legal_relationship_id) {
      this.claimTypeOptions = this.claimTypes.map((ct) => ct.id);
    } else {
      this.claimTypeOptions = this.claimTypes
        .map((ct) => ct.id)
        .filter(() =>
          this.claimTypeLegalRelationships.some(
            (ctlr) => ctlr.legal_relationship_id === legal_relationship_id,
          ),
        );
    }
  }

  private async setSzamlazzhuInvoices(): Promise<void> {
    if (!this.editCaseDialogService.data?.payee_case.is_szamlazzhu) {
      return;
    }

    this.szamlazzhuInvoices = [];
    let page = 0;
    let total: number;
    const per_page = 100;
    do {
      try {
        const invoices = await this.szamlazzhuService.getInvoiceFormDataTable(
          this.editCaseDialogService.data.payee_case.payee_case_reference_id,
          {
            page,
            per_page,
          },
        );
        this.szamlazzhuInvoices.push(...invoices.invoices);

        total = invoices.total;
        ++page;
      } catch (error) {
        console.error('Error while getting invoices', error);
        return;
      }
    } while (total > page * per_page);
  }
}
